import React from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { UserPlus, Plane, ShieldCheck, Info } from 'lucide-react';
import { createFlightOrder } from '../../services/flightService';
import { getSelectedOffer } from './FlightSearchPage';
import SliceRow from './SliceRow';
import { offerSlices } from './flightFilters';
import { CABIN_LABEL, PAX_LABEL, formatMoney, priceBreakdown } from './flightData';

const usePageTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

const emptyPassenger = (ptype) => ({
  ptype,
  last_name_zh: '',
  first_name_zh: '',
  last_name_en: '',
  first_name_en: '',
  gender: 'M',
  dob: '',
  nationality: 'CN',
  passport_no: '',
  passport_expiry: '',
  phone: '',
});

const PassengerForm = ({ idx, pax, errors, onChange }) => {
  const update = (patch) => onChange(idx, { ...pax, ...patch });
  const inputCls = (err) => `w-full h-11 px-3 border rounded-xl bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent ${err ? 'border-red-400' : 'border-gray-200'}`;
  const labelCls = 'block text-xs text-gray-500 mb-1';

  return (
    <div className="rounded-2xl border border-sky-100 bg-white p-5">
      <div className="flex items-center gap-2 font-bold text-gray-900 mb-4">
        <UserPlus className="w-4 h-4 text-sky-500" />
        乘机人 {idx + 1}
        <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">{PAX_LABEL[pax.ptype]}</span>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>姓（中文，选填）</label>
          <input value={pax.last_name_zh} onChange={(e) => update({ last_name_zh: e.target.value })} className={inputCls()} placeholder="张" />
        </div>
        <div>
          <label className={labelCls}>名（中文，选填）</label>
          <input value={pax.first_name_zh} onChange={(e) => update({ first_name_zh: e.target.value })} className={inputCls()} placeholder="三" />
        </div>
        <div>
          <label className={labelCls}>性别 *</label>
          <select value={pax.gender} onChange={(e) => update({ gender: e.target.value })} className={inputCls()}>
            <option value="M">男</option>
            <option value="F">女</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>姓（护照拼音） *</label>
          <input value={pax.last_name_en} onChange={(e) => update({ last_name_en: e.target.value.toUpperCase() })} className={inputCls(errors[idx]?.last_name_en)} placeholder="ZHANG" />
          {errors[idx]?.last_name_en && <p className="text-xs text-red-500 mt-1">{errors[idx].last_name_en}</p>}
        </div>
        <div>
          <label className={labelCls}>名（护照拼音） *</label>
          <input value={pax.first_name_en} onChange={(e) => update({ first_name_en: e.target.value.toUpperCase() })} className={inputCls(errors[idx]?.first_name_en)} placeholder="SAN" />
          {errors[idx]?.first_name_en && <p className="text-xs text-red-500 mt-1">{errors[idx].first_name_en}</p>}
        </div>
        <div>
          <label className={labelCls}>出生日期 *</label>
          <input type="date" value={pax.dob} onChange={(e) => update({ dob: e.target.value })} className={inputCls(errors[idx]?.dob)} />
          {errors[idx]?.dob && <p className="text-xs text-red-500 mt-1">{errors[idx].dob}</p>}
        </div>
        <div>
          <label className={labelCls}>护照号 *</label>
          <input value={pax.passport_no} onChange={(e) => update({ passport_no: e.target.value.toUpperCase() })} className={inputCls(errors[idx]?.passport_no)} placeholder="E12345678" />
          {errors[idx]?.passport_no && <p className="text-xs text-red-500 mt-1">{errors[idx].passport_no}</p>}
        </div>
        <div>
          <label className={labelCls}>护照有效期至 *</label>
          <input type="date" value={pax.passport_expiry} onChange={(e) => update({ passport_expiry: e.target.value })} className={inputCls(errors[idx]?.passport_expiry)} />
          {errors[idx]?.passport_expiry && <p className="text-xs text-red-500 mt-1">{errors[idx].passport_expiry}</p>}
        </div>
        <div>
          <label className={labelCls}>手机号（选填）</label>
          <input value={pax.phone} onChange={(e) => update({ phone: e.target.value })} className={inputCls()} placeholder="13800138000" />
        </div>
      </div>
    </div>
  );
};

const FlightBookingPage = () => {
  usePageTitle('填写订单 | 旅报报·机票');
  const navigate = useNavigate();
  const selected = getSelectedOffer();

  const { offer, criteria } = selected || {};
  const clientRef = React.useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const [passengers, setPassengers] = React.useState(() => {
    if (!criteria) return [];
    const list = [];
    for (let i = 0; i < Number(criteria.adults || 1); i++) list.push(emptyPassenger('adult'));
    for (let i = 0; i < Number(criteria.children || 0); i++) list.push(emptyPassenger('child'));
    for (let i = 0; i < Number(criteria.infants || 0); i++) list.push(emptyPassenger('infant'));
    return list;
  });
  const [contact, setContact] = React.useState({ name: '', phone: '', email: '' });
  const [remark, setRemark] = React.useState('');
  const [errors, setErrors] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');

  if (!selected || !offer) {
    return <Navigate to="/flight" replace />;
  }

  const totalPax = passengers.length;
  // 供应商给了全员权威总价就直接用；没有才按成人/儿童/婴儿比例估算（与后端 _calc_total 一致）
  const hasVendorTotal = offer.total_amount !== null && offer.total_amount !== undefined;
  const estTotal = hasVendorTotal ? offer.total_amount : Math.round(
    offer.price_per_adult * Number(criteria.adults || 1)
    + offer.price_per_adult * 0.75 * Number(criteria.children || 0)
    + offer.price_per_adult * 0.10 * Number(criteria.infants || 0)
  );

  const bookingSlices = offerSlices(offer);
  const price = priceBreakdown(offer);

  const updatePax = (idx, pax) => {
    setPassengers((list) => list.map((p, i) => (i === idx ? pax : p)));
  };

  const validate = () => {
    const errs = passengers.map((p) => {
      const e = {};
      if (!p.last_name_en?.trim()) e.last_name_en = '必填，请按护照拼音填写';
      if (!p.first_name_en?.trim()) e.first_name_en = '必填，请按护照拼音填写';
      if (!p.dob) e.dob = '必填';
      if (!p.passport_no?.trim()) e.passport_no = '必填';
      if (!p.passport_expiry) e.passport_expiry = '必填';
      else if (criteria?.depart_date && p.passport_expiry < criteria.depart_date) e.passport_expiry = '护照有效期须晚于出发日期';
      return e;
    });
    setErrors(errs);
    const contactErr = {};
    if (!contact.name?.trim()) contactErr.name = '必填';
    if (!contact.phone?.trim()) contactErr.phone = '必填';
    const paxValid = errs.every((e) => Object.keys(e).length === 0);
    return paxValid && Object.keys(contactErr).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitError('');
    if (!validate()) {
      setSubmitError('请补全带 * 的必填项后再提交');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    try {
      const order = await createFlightOrder({
        offer_id: offer.offer_id,
        // 行程类型必须如实传：写死 'ow' 会把往返/多程订单存成单程，订单页行程就错了
        search: {
          origin: criteria.origin,
          destination: criteria.destination,
          depart_date: criteria.depart_date,
          trip_type: criteria.trip_type || 'ow',
          return_date: criteria.trip_type === 'rt' ? criteria.return_date : undefined,
          legs: criteria.trip_type === 'mc' ? criteria.legs : undefined,
          cabin: criteria.cabin,
          adults: Number(criteria.adults || 1),
          children: Number(criteria.children || 0),
          infants: Number(criteria.infants || 0),
        },
        passengers: passengers.map((p) => ({
          ...p,
          dob: p.dob,
          passport_expiry: p.passport_expiry,
        })),
        contact,
        client_ref: clientRef.current,
        remark: remark || null,
      });
      sessionStorage.removeItem('flight:selected');
      navigate(`/flight/orders?highlight=${order.order_id}`);
    } catch (err) {
      setSubmitError(err.message || '提交失败，请稍后重试');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (err) => `w-full h-11 px-3 border rounded-xl bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent ${err ? 'border-red-400' : 'border-gray-200'}`;
  const labelCls = 'block text-xs text-gray-500 mb-1';

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-cyan-50 to-white">
      <header className="sticky top-0 z-40 backdrop-blur bg-white/80 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="旅报报 logo" className="w-9 h-9" />
            <div className="font-extrabold text-gray-900">填写订单</div>
          </Link>
          <Link to="/flight" className="text-sm text-gray-600 hover:text-gray-900">← 重新选择航班</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {submitError && (
          <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">{submitError}</div>
        )}

        {/* 航班摘要 */}
        <div className="rounded-3xl bg-white border border-sky-100 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 font-bold text-gray-900 mb-4">
            <Plane className="w-5 h-5 text-sky-500" /> 已选航班
          </div>
          <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
            <div className="space-y-4">
              <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
                {CABIN_LABEL[offer.segments[0].cabin] || '经济舱'}
              </span>
              {/* 按行程方向分别展示，往返票不能把去程出发和回程到达拼成一行 */}
              {bookingSlices.map((sl, i) => (
                <SliceRow
                  key={i}
                  slice={sl}
                  label={bookingSlices.length > 1 ? (bookingSlices.length === 2 ? (i === 0 ? '去程' : '返程') : `第 ${i + 1} 程`) : null}
                  expanded
                />
              ))}
            </div>
            <div className="md:text-right">
              <p className="text-3xl font-black text-sky-600">
                {formatMoney(price.perAdult, price.currency)}
                <span className="text-sm text-gray-400 font-medium">/成人</span>
              </p>
              <p className="text-xs text-gray-400">
                票价 {formatMoney(price.fare, price.currency)} + 税费 {formatMoney(price.tax, price.currency)}
              </p>
              <p className="text-xs text-gray-400">余票 {offer.seats_left} 张</p>
            </div>
          </div>
          <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-700 flex items-start gap-2">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            报价为航司实时价格，有效期约 30 分钟。提交后订单为「待确认」状态，客服人工复核价格与舱位后与您联系，确认前不收款。
          </div>
        </div>

        <form onSubmit={submit} className="space-y-6">
          {/* 乘机人 */}
          {passengers.map((pax, idx) => (
            <PassengerForm
              key={idx}
              idx={idx}
              pax={pax}
              errors={errors}
              onChange={updatePax}
            />
          ))}

          <div className="rounded-2xl bg-sky-50/60 border border-sky-100 p-4 text-sm text-gray-600 flex items-center justify-between gap-3">
            <span>
              本次报价按 <b>成人 {criteria.adults || 1}</b>
              {Number(criteria.children) > 0 && <> · 儿童 {criteria.children}</>}
              {Number(criteria.infants) > 0 && <> · 婴儿 {criteria.infants}</>}
              {' '}计价，如需增减乘机人请返回重新查询
            </span>
            <Link
              to="/flight"
              className="flex-shrink-0 px-4 py-2 rounded-xl border border-sky-200 text-sky-700 font-medium hover:bg-white"
            >
              修改人数
            </Link>
          </div>

          {/* 联系人 */}
          <div className="rounded-2xl border border-sky-100 bg-white p-5">
            <div className="flex items-center gap-2 font-bold text-gray-900 mb-4">
              <ShieldCheck className="w-4 h-4 text-sky-500" /> 联系人（用于确认订单与出票通知）
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>姓名 *</label>
                <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className={inputCls(!contact.name?.trim() && errors._contact)} />
              </div>
              <div>
                <label className={labelCls}>手机号 *</label>
                <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className={inputCls()} placeholder="用于客服联系" />
              </div>
              <div>
                <label className={labelCls}>邮箱（选填）</label>
                <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls()} />
              </div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>备注（选填，如行李、座位偏好）</label>
              <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows="2"
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
          </div>

          {/* 提交区 */}
          <div className="rounded-3xl bg-white border border-sky-100 shadow-sm p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">共 {totalPax} 位乘机人（成人 {criteria.adults} · 儿童 {criteria.children || 0} · 婴儿 {criteria.infants || 0}）</p>
              <p className="text-3xl font-black text-gray-900">
                预估总价 {formatMoney(estTotal, offer.currency)}
                <span className="text-sm text-gray-400 font-medium ml-2">
                  {hasVendorTotal ? '（含全部乘机人，以客服确认为准）' : '（儿童 75% · 婴儿 10%，以客服确认为准）'}
                </span>
              </p>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="px-10 py-4 rounded-2xl bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-bold text-lg hover:from-sky-600 hover:to-cyan-600 hover:shadow-lg transition-all disabled:opacity-60"
            >
              {submitting ? '提交中…' : '提交订单'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default FlightBookingPage;
